import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { ApiContext } from "../../../../../context/MyContext";
import DateBox from "../../../../../common/DateBox/DateBox";
import Loader from "../../../../../common/Loader/Loader";
// Shimmed rather than raw so this tab keeps working if it is ever composed
// into an embedded page; outside a flow both resolve to the real router.
import {
  useRouteLocation as useLocation,
  useFlowNavigate,
} from "../../../../../common/embeddedFlow/embeddedRoute";
import { formatDate } from "../../../../../utils/validation.util";
import { formatLocalDate } from "../../../../../utils/formatLocalDate";
import TrackedDisplay from "./TrackedDisplay";
import PlannedWorkoutPlansTable from "./PlannedWorkoutPlansTable";
import { useUtils } from "../../../../../context/UtilsContext";
import {
  fetchDailyWorkoutDetails,
  fetchExerciseDetailsByIds,
  AssignedPlans,
} from "../../../core/api/workoutApi";
import { useWorkoutPlanEventTypeId } from "../../plan/eventTypes";
import { WORKOUT_ROUTES } from "../../../core/constants/routes";
import { hasRouteAccess } from "../../../../../utils/accessControl";
import {
  displayDate,
  durationPlanCategory,
} from "../../../core/utils/assignedPlansUtils";
import { durationPlanName } from "../../../core/utils/durationPlanName";
import {
  resolvePlanCategory,
  filterSessionsByCategory,
} from "../../../core/utils/planCategory";
import CopyWorkoutPlanModal from "../../plan/modify/CopyWorkoutPlanModal";

const ActivityTab = ({
  profileId,
  variant = "full",
  // Where the plan editor sends the coach when it finishes. Defaults to the
  // full user page; the Users split view overrides it with its own URL so a
  // round trip through the editor lands back on the open panel.
  returnTo: returnToProp,
}) => {
  const isCompact = variant === "compact";
  const [visibleDates, setVisibleDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const { get, post, userId, employeeData, departmentsWithRoles } =
    useContext(ApiContext);
  // "Add Workout Plan" opens the workout template list, so offer it only to
  // callers who can reach that list. Before employee data resolves there is
  // nothing to check against, so don't hide it (same as AppSidebar).
  const canAssignWorkoutPlan =
    !employeeData?.data ||
    hasRouteAccess(
      WORKOUT_ROUTES.TEMPLATES_LIST,
      employeeData,
      departmentsWithRoles
    );
  // Prefer an explicit profileId (e.g. the comms client rail) and fall back to
  // the logged-in user id from context to preserve existing dashboard behavior.
  const effectiveUserId = profileId || userId;
  const returnTo = returnToProp || `/dashboard/${effectiveUserId}`;
  const { sessionUtils } = useUtils();
  const eventTypeId = useWorkoutPlanEventTypeId();
  const location = useLocation();
  const [isPlannedView, setIsPlannedView] = useState(false);
  const [planViewFilter, setPlanViewFilter] = useState("coach");

  const [trackedWorkouts, setTrackedWorkouts] = useState({});
  const [isLoadingTracked, setIsLoadingTracked] = useState(false);

  const [plansTableList, setPlansTableList] = useState([]);
  const [plansTableLoading, setPlansTableLoading] = useState(false);
  const [copySourceRow, setCopySourceRow] = useState(null);

  // Renders the plan editor inline when this tab is inside a surface that can
  // host it (the Users split panel), and routes normally otherwise — so
  // /dashboard/:id behaves exactly as before.
  const navigate = useFlowNavigate();
  const trackedCache = useRef(new Map());

  // Mirrors the app's own _isSetCompleted (exercise_item.dart): a strict
  // actual >= target check, branching on whichever target is set — reps take
  // priority when present, otherwise it's judged on time. No partial-credit
  // threshold, since the app doesn't apply one either.
  const isSetCompleted = (set, targetSet) => {
    const targetReps = targetSet?.target_reps || 0;
    if (targetReps > 0) {
      const actualReps = set.actual_reps || 0;
      return actualReps >= targetReps;
    }

    const targetTimeSeconds = targetSet?.target_time_seconds || 0;
    if (targetTimeSeconds > 0) {
      const actualTimeSeconds = set.actual_time_seconds || 0;
      return actualTimeSeconds >= targetTimeSeconds;
    }

    return false;
  };

  const transformTrackedWorkouts = async (dailyData, selectedDate) => {
    try {
      const dayData = dailyData.find((day) => day.date === selectedDate);
      if (!dayData?.actual_trackings?.length) return { sessions: [] };

      const allWorkoutIds = new Set();
      dayData.actual_trackings.forEach((tracking) => {
        tracking.workout_groups.forEach((group) => {
          group.workouts.forEach((workout) => {
            if (workout.actual_workout_id)
              allWorkoutIds.add(workout.actual_workout_id);
          });
        });
      });

      const workoutIds = Array.from(allWorkoutIds);
      let exerciseDetailsMap = {};

      if (workoutIds.length > 0) {
        try {
          const exerciseDetailsResponse = await fetchExerciseDetailsByIds(
            post,
            workoutIds
          );
          if (
            exerciseDetailsResponse?.data?.results &&
            Array.isArray(exerciseDetailsResponse.data.results)
          ) {
            exerciseDetailsResponse.data.results.forEach((exercise) => {
              const exerciseId = exercise.exercise_id || exercise.id;
              exerciseDetailsMap[exerciseId] = exercise;
            });
          }
        } catch (error) {
          console.warn(
            "Failed to fetch exercise details for tracked workouts:",
            error
          );
        }
      }

      // set_id -> targetSet (for target_reps/weight/rest lookups)
      const targetSetsMap = {};
      // target_plan_id -> { planCategory, planTitle }
      const planLookup = {};
      if (dayData.target_plans) {
        dayData.target_plans.forEach((plan) => {
          const planCategory = resolvePlanCategory(
            plan,
            dayData.assigned_to_id
          );
          const planTitle = plan.plan_title || plan.title || plan.name || null;
          const planId = plan.target_plan_id ?? plan.id ?? null;
          planLookup[planId] = { planCategory, planTitle };
          plan.workout_groups.forEach((group) => {
            group.workouts.forEach((workout) => {
              workout.sets.forEach((set) => {
                targetSetsMap[set.target_plan_workout_set_id] = set;
              });
            });
          });
        });
      }

      const transformedSessions = [];

      dayData.actual_trackings.forEach((tracking) => {
        // Resolved once per tracking record (which always belongs to exactly
        // one plan via tracking.target_plan_id) rather than per exercise/set —
        // exercises with no logged sets (workout stopped early) still resolve
        // correctly this way, since they carry no set-level data to infer from.
        const { planCategory = "coach", planTitle = null } =
          planLookup[tracking.target_plan_id] || {};

        const sessionGroups = [];

        tracking.workout_groups.forEach((group) => {
          const exercises = group.workouts.map((workout) => {
            const exerciseDetails =
              exerciseDetailsMap[workout.actual_workout_id];

            const completedSets = workout.sets.filter((set) => {
              const targetSet =
                targetSetsMap[set.target_plan_workout_set_id];
              return isSetCompleted(set, targetSet);
            }).length;

            const totalSets = workout.sets.length;
            const progressPercentage =
              totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

            return {
              exercise_name:
                exerciseDetails?.exercise ||
                exerciseDetails?.name ||
                workout.workout_name ||
                "Unnamed Exercise",
              difficulty: exerciseDetails?.difficulty || "Unknown",
              target_muscle: exerciseDetails?.target_muscle || "N/A",
              rest_time_after: workout.rest_time_after_seconds || 0,
              progress_percentage: progressPercentage,
              sets: workout.sets.map((set) => {
                const targetSet =
                  targetSetsMap[set.target_plan_workout_set_id];
                const targetReps = targetSet?.target_reps || 0;
                const isCompleted = isSetCompleted(set, targetSet);

                return {
                  target_reps: targetReps,
                  actual_reps: set.actual_reps || null,
                  target_weight: targetSet?.target_weight || 0,
                  actual_weight: set.actual_weight || null,
                  target_rest_seconds: targetSet?.target_rest_seconds || 0,
                  actual_rest_seconds: set.actual_rest_time_seconds || null,
                  target_time_seconds: targetSet?.target_time_seconds || 0,
                  actual_time_seconds: set.actual_time_seconds || null,
                  is_completed: isCompleted,
                };
              }),
            };
          });

          sessionGroups.push({
            group_name: group.group_type_description || "Workout Group",
            exercises: exercises,
          });
        });

        transformedSessions.push({
          planTrackingId: tracking.plan_tracking_id,
          planTitle,
          planCategory,
          groups: sessionGroups,
        });
      });

      return { sessions: transformedSessions };
    } catch (error) {
      console.error("Error transforming tracked workouts:", error);
      return { sessions: [] };
    }
  };

  const fetchPlannedPlansTable = useCallback(async () => {
    if (!eventTypeId || !effectiveUserId) return;
    setPlansTableLoading(true);
    try {
      const today = new Date();
      const startWide = new Date(today);
      startWide.setDate(startWide.getDate() - 365);
      const endWide = new Date(today);
      endWide.setDate(endWide.getDate() + 365);

      const response = await AssignedPlans(
        get,
        formatLocalDate(startWide),
        formatLocalDate(endWide),
        effectiveUserId,
        eventTypeId
      );

      const durations = response?.data?.durations || [];
      const rows = durations
        .map((d) => ({
          id: d.duration_id,
          duration_id: d.duration_id,
          start_date: d.start_date,
          end_date: d.end_date,
          total_plans: d.total_plans ?? 0,
          weekday_count: d.weekday_count ?? 0,
          weekdays: d.weekdays || [],
          planName: durationPlanName(d),
          title: d.title || d.name || d.plan_name || null,
          planCategory: durationPlanCategory(d, effectiveUserId),
        }))
        .sort((a, b) =>
          String(b.start_date || "").localeCompare(String(a.start_date || ""))
        );

      setPlansTableList(rows);
    } catch (err) {
      console.error("Error fetching planned plans table:", err);
      setPlansTableList([]);
    } finally {
      setPlansTableLoading(false);
    }
  }, [eventTypeId, effectiveUserId, get]);

  const handleSelectDuration = useCallback(
    (row) => {
      if (!row?.duration_id) return;
      navigate(`/dashboard/${effectiveUserId}/workout-plan/${row.duration_id}`, {
        state: {
          durationMeta: {
            [row.duration_id]: {
              duration_id: row.duration_id,
              start_date: row.start_date,
              end_date: row.end_date,
              total_plans: row.total_plans,
              weekday_count: row.weekday_count,
              weekdays: row.weekdays || [],
              title: row.planName || row.title || null,
            },
          },
          returnTo,
        },
      });
    },
    [navigate, effectiveUserId, returnTo]
  );

  const handleCopyDuration = useCallback((row) => {
    if (!row?.duration_id) return;
    setCopySourceRow(row);
  }, []);

  const handleCopyContinue = useCallback(
    ({ targetUserId }) => {
      const row = copySourceRow;
      if (!row?.duration_id || !targetUserId) return;

      const start = displayDate(row.start_date);
      const end = displayDate(row.end_date);
      const label =
        start && end && start !== end ? `${start} – ${end}` : start || end || "previous plan";

      const fromDate = new Date();
      const toDate = new Date();
      toDate.setDate(toDate.getDate() + 7);

      setCopySourceRow(null);
      navigate(WORKOUT_ROUTES.PLAN_EDITOR, {
        state: {
          openCreatePlan: true,
          copyFromDuration: true,
          sourceDuration: {
            duration_id: row.duration_id,
            start_date: row.start_date,
            end_date: row.end_date,
            weekdays: row.weekdays || [],
            title: row.planName,
          },
          sourceDurationLabel: row.planName
            ? `${row.planName} · ${label}`
            : label,
          suggestedPlanName: row.planName ? `Copy · ${row.planName}` : `Copy · ${label}`,
          // Target gets the new plan; source owner is needed to load workouts.
          sourceClientId: effectiveUserId,
          fromUserPage: true,
          clientId: targetUserId,
          returnTo: `/dashboard/${targetUserId}`,
          fromDate,
          toDate,
        },
      });
    },
    [copySourceRow, navigate, effectiveUserId]
  );

  const updateTrackedDateRange = useCallback(() => {
    const today = new Date();
    const dates = [];
    for (let i = -12; i <= 0; i++) {
      const newDate = new Date(today);
      newDate.setDate(today.getDate() + i);
      dates.push(newDate);
    }
    setVisibleDates(dates);
  }, []);

  const fetchTrackedForRange = async () => {
    if (visibleDates.length === 0) return;
    try {
      setIsLoadingTracked(true);

      const fromDate = formatDate(visibleDates[0]);
      const toDate = formatDate(visibleDates[visibleDates.length - 1]);

      const eventTypes = sessionUtils?.event_types?.[0]?.event_types || [];
      const trackingEventTypeId = eventTypes.length > 0 ? eventTypes[0].id : null;
      if (!trackingEventTypeId) return;

      const cacheKey = `${fromDate}_${toDate}_tracked`;
      if (trackedCache.current.has(cacheKey)) {
        setTrackedWorkouts(trackedCache.current.get(cacheKey));
        setIsLoadingTracked(false);
        return;
      }

      const response = await fetchDailyWorkoutDetails(
        get,
        trackingEventTypeId,
        fromDate,
        toDate,
        effectiveUserId
      );

      if (response.success && response.data) {
        const mapped = {};
        for (const day of response.data) {
          mapped[day.date] = await transformTrackedWorkouts(
            response.data,
            day.date
          );
        }
        trackedCache.current.set(cacheKey, mapped);
        setTrackedWorkouts(mapped);
      }
    } catch (err) {
      console.error("Error fetching tracked workouts:", err);
    } finally {
      setIsLoadingTracked(false);
    }
  };

  useEffect(() => {
    if (!isPlannedView) updateTrackedDateRange();
  }, [isPlannedView, updateTrackedDateRange]);

  useEffect(() => {
    if (isPlannedView) {
      fetchPlannedPlansTable();
    }
  }, [isPlannedView, fetchPlannedPlansTable]);

  useEffect(() => {
    if (!isPlannedView && visibleDates.length > 0) {
      fetchTrackedForRange();
    }
  }, [visibleDates, isPlannedView]); // eslint-disable-line

  useEffect(() => {
    if (location.state?.workoutPlanAssigned) {
      trackedCache.current.clear();
      setIsPlannedView(true);
      fetchPlannedPlansTable();
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location.state?.workoutPlanAssigned, location.pathname, location.search, navigate, fetchPlannedPlansTable]);

  const handleChangeTracked = () => setIsPlannedView(false);
  const handleChangePlanned = () => setIsPlannedView(true);
  const handleOnSelectDate = (date) => setSelectedDate(date);

  const currentTrackedRaw = trackedWorkouts?.[selectedDate] || [];
  const currentTracked = currentTrackedRaw?.sessions
    ? {
      ...currentTrackedRaw,
      sessions: filterSessionsByCategory(
        currentTrackedRaw.sessions,
        planViewFilter
      ),
    }
    : currentTrackedRaw;

  const PLAN_VIEW_FILTERS = [
    { key: "coach", label: "Coach Plans" },
    { key: "personal", label: "Personal Plans" },
  ];
  const emptyTrackedMessage =
    planViewFilter === "coach"
      ? "No coach-plan workouts for this date"
      : "No personal-plan workouts for this date";
  const filteredPlansTableList = filterSessionsByCategory(
    plansTableList,
    planViewFilter
  );
  const emptyPlannedMessage =
    planViewFilter === "coach"
      ? "No coach-assigned plans for this filter."
      : "No personal plans for this filter.";

  return (
    <div className={`w-full app-bg ${isCompact ? "p-3" : "min-h-screen pb-8"}`}>
      <div
        className={`flex flex-wrap items-center gap-4 my-6 px-2 ${isCompact ? "gap-2 my-4" : "justify-between"
          }`}
      >
        <div className={`flex ${isCompact ? "gap-2 w-full" : "gap-3"}`}>
          <button
            className={`${isCompact ? "flex-1 px-3 py-2 text-sm" : "w-[180px] px-6 py-3"} ${!isPlannedView
              ? "bg-dark-orange text-white"
              : "bg-white text-gray-700"
              } rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200`}
            onClick={handleChangeTracked}
          >
            Tracked
          </button>
          <button
            className={`${isCompact ? "flex-1 px-3 py-2 text-sm" : "w-[180px] px-6 py-3"} ${isPlannedView
              ? "bg-dark-orange text-white"
              : "bg-white text-gray-700"
              } rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200`}
            onClick={handleChangePlanned}
          >
            Planned
          </button>
        </div>
        {canAssignWorkoutPlan && (
          <button
            className={`${
              isCompact ? "w-full px-4 py-2 text-sm" : "w-[200px] px-6 py-3"
            } bg-orange-primary text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200`}
            onClick={() => {
              const fromDate = new Date();
              const toDate = new Date();
              toDate.setDate(toDate.getDate() + 7);

              navigate("/fitness/list", {
                state: {
                  fromUserPage: true,
                  clientId: effectiveUserId,
                  returnTo,
                  fromDate,
                  toDate,
                },
              });
            }}
          >
            Add Workout Plan
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 px-2">
        {PLAN_VIEW_FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`${
              isCompact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"
            } ${
              planViewFilter === key
                ? "bg-dark-orange text-white"
                : "bg-white text-gray-700 border border-gray-200"
            } rounded-lg font-semibold shadow-sm transition-all duration-200`}
            onClick={() => setPlanViewFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {isPlannedView ? (
        <div className="w-full px-2">
          <p className="text-sm text-gray-500 mb-3">
            Weekly workout schedules for this user. Click a row to view and edit.
          </p>
          <PlannedWorkoutPlansTable
            tableData={filteredPlansTableList}
            loading={plansTableLoading}
            onSelectDuration={handleSelectDuration}
            onCopyDuration={handleCopyDuration}
            copyingDurationId={copySourceRow?.duration_id}
            emptyMessage={emptyPlannedMessage}
          />
          <CopyWorkoutPlanModal
            isOpen={Boolean(copySourceRow)}
            onClose={() => setCopySourceRow(null)}
            onContinue={handleCopyContinue}
            currentUserId={userId}
            planName={copySourceRow?.planName || "Workout plan"}
            sourceLabel={
              copySourceRow
                ? `${displayDate(copySourceRow.start_date)}${copySourceRow.start_date !== copySourceRow.end_date
                  ? ` – ${displayDate(copySourceRow.end_date)}`
                  : ""
                }`
                : ""
            }
          />
          {canAssignWorkoutPlan && !plansTableLoading && plansTableList.length === 0 && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => {
                  const fromDate = new Date();
                  const toDate = new Date();
                  toDate.setDate(toDate.getDate() + 7);
                  navigate("/fitness/list", {
                    state: {
                      fromUserPage: true,
                      clientId: effectiveUserId,
                      returnTo,
                      fromDate,
                      toDate,
                    },
                  });
                }}
                className="px-4 py-2 rounded-lg bg-orange-primary text-white font-medium hover:bg-orange-600"
              >
                Add Workout Plan
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="relative mb-6 px-2">
            <div className="flex overflow-x-auto py-3 scrollbar-hide gap-2">
              {visibleDates.map((date, index) => (
                <DateBox
                  key={index}
                  date={date.getDate()}
                  month={date.toLocaleString("default", { month: "short" })}
                  year={date.getFullYear()}
                  selected={selectedDate === formatDate(date)}
                  OnSelect={() => handleOnSelectDate(formatDate(date))}
                />
              ))}
            </div>
          </div>

          <div className="w-full px-2">
            {isLoadingTracked ? (
              <Loader />
            ) : currentTracked?.sessions?.length > 0 ? (
              <TrackedDisplay trackedData={currentTracked} />
            ) : (
              <div className="text-center py-14 bg-gray-50 rounded-xl border border-gray-200">
                <svg
                  className="w-20 h-20 mx-auto mb-4 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="text-gray-600 text-xl font-semibold mb-2">
                  {emptyTrackedMessage}
                </p>
                <p className="text-gray-400 text-sm">
                  Start tracking your workouts to see them here
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityTab;
